import { datadogRum } from '@datadog/browser-rum';

import { urlPagamentoSegura } from '@/shared/utils/pagamentoUrl';
import { reportError } from '@/shared/utils/reportError';

import { OBJETIVOS_VALIDOS, SELF_TERMOS_VERSAO } from '../constants';
import type { ObjetivoSelf, SessaoSelf } from '../types';

// TODO: migrar o accessToken do lead para cookie httpOnly emitido pelo core-api (core-api#1044) e remover a credencial do web storage.
// TODO: extrair wrapper genérico de storage JSON para shared/utils e migrar as 7 cópias (issue #737).
const CHAVE = 'negociacaoSelf:sessao';
// 2: a sessão guarda a variação do catálogo (planos de 2026-11) no lugar de tier/período; a
// sessão da versão 1 é descartada, para nenhuma aba retomar o checkout com o tier antigo.
const VERSAO_SCHEMA = 2;
const SESSAO_TTL_MS = 20 * 60 * 1000;

interface SessaoSelfPersistida extends SessaoSelf {
  versao: number;
  salvoEm: number;
  termosVersao: string | null;
}

const objetivoValido = (valor: unknown): valor is ObjetivoSelf =>
  typeof valor === 'string' && (OBJETIVOS_VALIDOS as readonly string[]).includes(valor);

export function salvarSessaoSelf(parcial: Partial<SessaoSelf>): void {
  try {
    const registroAnterior = lerSessaoSelf();
    const trocaDeLead = Boolean(
      parcial.leadId && registroAnterior && parcial.leadId !== registroAnterior.leadId,
    );
    const atual = trocaDeLead ? null : registroAnterior;
    const leadId = parcial.leadId ?? atual?.leadId;
    const accessToken = parcial.accessToken ?? atual?.accessToken;
    const objetivo = parcial.objetivo ?? atual?.objetivo;
    if (!leadId || !accessToken || !objetivo) return;

    const invoiceUrl = parcial.invoiceUrl ?? atual?.invoiceUrl ?? null;
    const termosAceitos = parcial.termosAceitos ?? atual?.termosAceitos ?? false;
    const persistida: SessaoSelfPersistida = {
      versao: VERSAO_SCHEMA,
      salvoEm: Date.now(),
      leadId,
      accessToken,
      objetivo,
      invoiceUrl: urlPagamentoSegura(invoiceUrl) ? invoiceUrl : null,
      variationId: parcial.variationId ?? atual?.variationId ?? null,
      termosAceitos,
      termosVersao: termosAceitos ? SELF_TERMOS_VERSAO : null,
    };
    window.sessionStorage.setItem(CHAVE, JSON.stringify(persistida));
  } catch (error) {
    reportError(error instanceof Error ? error : new Error(String(error)), {
      source: 'negociacaoSelf.salvarSessao',
    });
  }
}

const descartarSessaoInvalida = (motivo: string): null => {
  limparSessaoSelf();
  try {
    // TODO: migrar para registrarAcao de shared/utils/reportAction (#772)
    datadogRum.addAction('negociacaoSelf.sessaoDescartada', { motivo });
  } catch {
    return null;
  }
  return null;
};

export function lerSessaoSelf(): SessaoSelf | null {
  let bruto: string | null;
  try {
    bruto = window.sessionStorage.getItem(CHAVE);
  } catch {
    return descartarSessaoInvalida('storage_indisponivel');
  }
  if (!bruto) return null;

  let parseado: unknown;
  try {
    parseado = JSON.parse(bruto);
  } catch {
    return descartarSessaoInvalida('json_invalido');
  }
  if (typeof parseado !== 'object' || parseado === null) {
    return descartarSessaoInvalida('schema');
  }
  const dados = parseado as Partial<SessaoSelfPersistida>;

  if (dados.versao !== VERSAO_SCHEMA) return descartarSessaoInvalida('schema');
  if (typeof dados.salvoEm !== 'number' || Date.now() - dados.salvoEm > SESSAO_TTL_MS) {
    return descartarSessaoInvalida('ttl');
  }
  if (typeof dados.leadId !== 'string' || !dados.leadId) return descartarSessaoInvalida('schema');
  if (typeof dados.accessToken !== 'string' || !dados.accessToken) {
    return descartarSessaoInvalida('schema');
  }
  if (!objetivoValido(dados.objetivo)) return descartarSessaoInvalida('objetivo_invalido');

  return {
    leadId: dados.leadId,
    accessToken: dados.accessToken,
    objetivo: dados.objetivo,
    invoiceUrl: urlPagamentoSegura(dados.invoiceUrl) ? dados.invoiceUrl : null,
    variationId:
      typeof dados.variationId === 'string' && dados.variationId ? dados.variationId : null,
    termosAceitos: dados.termosAceitos === true && dados.termosVersao === SELF_TERMOS_VERSAO,
  };
}

export function limparSessaoSelf(): void {
  try {
    window.sessionStorage.removeItem(CHAVE);
  } catch {
    return;
  }
}

export function limparSessaoSelfDaAba(aba: Window): void {
  try {
    aba.sessionStorage.removeItem(CHAVE);
  } catch {
    return;
  }
}
